/**
 * Page context for Salesforce Data Cloud website engagement events.
 *
 * Route pages know which Sanity document type they render (`page`, `article`,
 * `caseStudy`, ...). The tracking code lives in the root layout, outside the
 * page tree, so the route hands that type over through this tiny module-level
 * store (see `AnalyticsPageContext`). `salesforceInteractions` reads it when
 * it builds a page-view so `source.pageType` carries the document type instead
 * of one constant for every page.
 *
 * Everything here sits behind `NEXT_PUBLIC_SF_PAGE_CONTEXT` (default off). With
 * the flag off nothing is stored and the legacy constant is always reported.
 */

/** What every page reported before page context existed. */
export const DEFAULT_SOURCE_PAGE_TYPE = "standard__externalWebPage";

/** Sanity document types are plain identifiers; anything else is not trusted. */
const DOCUMENT_TYPE_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

let currentDocumentType: string | undefined;
let currentOwner = 0;
let lastOwner = 0;

/**
 * Whether page-context tracking is on. Read at call time (not module load) so
 * tests can toggle it; Next inlines the literal `process.env.NEXT_PUBLIC_*`
 * reference into the client bundle at build time.
 */
export function isPageContextEnabled(): boolean {
    const value = process.env.NEXT_PUBLIC_SF_PAGE_CONTEXT?.trim().toLowerCase();
    return value === "1" || value === "true";
}

function normalizeDocumentType(documentType: string | null | undefined): string | undefined {
    if (typeof documentType !== "string") return undefined;
    return DOCUMENT_TYPE_PATTERN.test(documentType) ? documentType : undefined;
}

/**
 * Records the document type of the page now on screen and returns a function
 * that forgets it again. Releasing only takes effect if no newer page has
 * written since, so an old page's cleanup can never wipe the next page's value
 * however React orders the two. Nothing is recorded when the flag is off.
 */
export function setPageDocumentType(documentType: string | null | undefined): () => void {
    if (!isPageContextEnabled()) return () => {};
    const owner = ++lastOwner;
    currentOwner = owner;
    currentDocumentType = normalizeDocumentType(documentType);
    return () => {
        if (currentOwner === owner) {
            currentOwner = 0;
            currentDocumentType = undefined;
        }
    };
}

/** The Sanity document type of the current page, if a route supplied one. */
export function getPageDocumentType(): string | undefined {
    return isPageContextEnabled() ? currentDocumentType : undefined;
}

/**
 * The value for `source.pageType`: the page's Sanity document type when the
 * flag is on and a route supplied one, otherwise the legacy constant.
 */
export function resolveSourcePageType(): string {
    return getPageDocumentType() ?? DEFAULT_SOURCE_PAGE_TYPE;
}
